<template>
  <div class="sign-contract-container">
    <QS-h4-title title="确认签约信息" />

    <el-form
      ref="formRef"
      :model="formData"
      :rules="rules"
      class="sign-contract-form"
      label-position="top"
    >
      <el-row :gutter="16">
        <el-col :span="12">
          <el-form-item label="签约主体">
            <span class="readonly-text">{{ contractInfo.merchantName || '--' }}</span>
          </el-form-item>
        </el-col>

        <el-col :span="12">
          <el-form-item label="司机姓名">
            <span class="readonly-text">{{ contractInfo.customerName || '--' }}</span>
          </el-form-item>
        </el-col>

        <el-col :span="12">
          <el-form-item :label="'签署方式' + columnSeparator" prop="signChannel">
            <el-radio-group v-model="formData.signChannel">
              <el-radio :label="SIGN_CHANNEL_MAP.ONLINE">线上签署</el-radio>
              <el-radio :label="SIGN_CHANNEL_MAP.OFFLINE">线下签署</el-radio>
            </el-radio-group>
          </el-form-item>
        </el-col>

        <el-col :span="12">
          <el-form-item :label="'换车日' + columnSeparator" prop="changeDate">
            <el-date-picker
              v-model="formData.changeDate"
              type="date"
              value-format="yyyy/MM/dd"
              format="yyyy/MM/dd"
              placeholder="请选择换车日"
              style="width: 100%;"
            />
          </el-form-item>
        </el-col>

        <el-col :span="12" v-if="formData.signChannel === SIGN_CHANNEL_MAP.ONLINE">
          <el-form-item :label="'合同模板' + columnSeparator" prop="templateId">
            <div class="preview-box" v-if="formData.templateId">
              <div class="preview-box__icon"></div>
              <div class="preview-box__content">
                <div class="preview-box__content__text">{{ formData.templateName }}</div>
              </div>
              <div class="preview-box__view" @click="goToPreviewTemplate">
                <i class="QS-icon-view"></i>
              </div>
            </div>
            <div class="template-error" v-else>{{ templateError || '未获取到合同模板' }}</div>
          </el-form-item>
        </el-col>

        <el-col :span="24" v-if="formData.signChannel === SIGN_CHANNEL_MAP.OFFLINE">
          <el-form-item :label="'合同附件' + columnSeparator" prop="attachment">
            <UploadCommon
              :fileList="formData.attachment"
              :limit="1"
              listType="text"
              :sizeType="4"
              acceptType=".pdf,.png,.jpg,.jpeg,.webp"
              commonUpload
              group="order"
              code="order_contract_attachment_file"
              @uploadSuccess="handleAttachmentChange"
              @deleteUploadHandle="handleAttachmentChange"
            />
            <div class="upload-tip">支持pdf和常用图片格式，文件大小不超过100MB</div>
          </el-form-item>
        </el-col>
      </el-row>
    </el-form>

    <qs-drawer
      :visible.sync="qrDrawerVisible"
      title="微信扫码签约"
      :append-to-body="true"
      :modal-append-to-body="true"
      cusSize="md"
    >
      <div class="qr-drawer-content">
        <div class="sign-instructions">
          <div class="top-info">
            <i class="el-icon-info info-icon"></i>
            <div class="info-content">
              <div class="info-text">1. 需签约司机使用【微信】扫码完成合同签署。</div>
              <div class="info-text">2. 签约有效期 7 天，超时后需要重新发起。</div>
            </div>
          </div>
        </div>

        <div class="qr-code-section">
          <div class="qr-code-container">
            <div class="qr-code-info">
              <div class="plate-no">{{ contractInfo.customerName || '--' }}</div>
              <div class="qr-desc">请使用微信扫码完成合同签署</div>
            </div>
            <div class="qr-code">
              <img
                v-if="getSignQrCodeUrl(contractInfo.signQrCode)"
                class="qr-placeholder"
                :src="getSignQrCodeUrl(contractInfo.signQrCode)"
                alt="签约二维码"
              />
              <div v-else class="qr-code-empty">暂无二维码</div>
            </div>
          </div>
        </div>
      </div>

      <template #footer>
        <el-button @click="qrDrawerVisible = false">取消</el-button>
        <el-button v-if="canCancelSign" :loading="cancelSignLoading" @click="cancelSignHandle">
          撤回签署
        </el-button>
        <el-button type="primary" :loading="refreshQrLoading" @click="refreshQrCodeStatus">
          刷新签署状态
        </el-button>
      </template>
    </qs-drawer>
  </div>
</template>

<script setup>
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import { Message, MessageBox } from 'element-ui'
import {
  cancelExchangeCarSign,
  genExchangeCarQrCode,
  getExchangeCarSignConfig,
  getExchangeCarSignState,
  offlineExchangeCarSign,
} from '@/api/domain/finance'
import { contractTemplateEffectPreviewV3Api } from '@/api/domain/organize'
import { SIGN_STATUS_ENUM } from '@/pages/orders_new/components/Step1/config'

const SIGN_CHANNEL_MAP = {
  OFFLINE: 1,
  ONLINE: 2,
}

const columnSeparator = '：'

const props = defineProps({
  orderId: {
    type: [String, Number],
    default: '',
  },
})
const emit = defineEmits(['sign-channel-change', 'sign-state-change'])

const formRef = ref(null)
const templateError = ref('')
const qrDrawerVisible = ref(false)
const refreshQrLoading = ref(false)
const cancelSignLoading = ref(false)
const canCancelSign = computed(() => {
  return [
    SIGN_STATUS_ENUM.WECHAT_QR_PENDING_SCAN,
    SIGN_STATUS_ENUM.PENDING_SIGN,
  ].includes(Number(contractInfo.value.flowState))
})

const createDefaultFormData = () => ({
  signChannel: SIGN_CHANNEL_MAP.ONLINE,
  changeDate: new Date(),
  templateId: '',
  templateName: '',
  attachment: [],
})

const formData = ref(createDefaultFormData())
const contractInfo = ref({
  merchantName: '',
  customerName: '',
  licenceNo: '',
  configSignChannel: '',
  signQrCode: '',
  contractFileUrl: '',
  signChannelName: '',
  flowState: '',
  flowStateName: '',
  esignAuthorized: false,
})

const rules = {
  signChannel: [
    { required: true, message: '请选择签署方式', trigger: 'change' },
  ],
  changeDate: [
    { required: true, message: '请选择换车日', trigger: 'change' },
  ],
  attachment: [
    {
      required: true,
      trigger: 'change',
      validator: (_rule, value, callback) => {
        if (formData.value.signChannel !== SIGN_CHANNEL_MAP.OFFLINE) {
          callback()
          return
        }

        if (!value || value.length === 0) {
          callback(new Error('请上传合同附件'))
          return
        }

        callback()
      },
    },
  ],
}

const getDefaultSignChannel = (data = {}) => {
  if (data?.signChannel) {
    return data.signChannel
  }

  if (data?.defaultSignChannel) {
    return data.defaultSignChannel
  }

  return data?.esignAuthorized ? SIGN_CHANNEL_MAP.ONLINE : SIGN_CHANNEL_MAP.OFFLINE
}

function formatDate(date) {
  const year = date.getFullYear()
  const month = `${date.getMonth() + 1}`.padStart(2, '0')
  const day = `${date.getDate()}`.padStart(2, '0')
  return `${year}/${month}/${day}`
}

function formatChangeDateForSubmit(date) {
  if (!date) return ''
  const dateStr = date instanceof Date ? formatDate(date) : `${date}`
  return dateStr.replace(/\//g, '-')
}

function getSignQrCodeUrl(signQrCode) {
  if (!signQrCode) {
    return ''
  }
  if (typeof signQrCode === 'string') {
    return signQrCode
  }
  return signQrCode?.qrCode || signQrCode?.url || ''
}

const resetData = () => {
  formData.value = createDefaultFormData()
  contractInfo.value = {
    merchantName: '',
    customerName: '',
    licenceNo: '',
    configSignChannel: '',
    signQrCode: '',
    contractFileUrl: '',
    signChannelName: '',
    flowState: '',
    flowStateName: '',
    esignAuthorized: false,
  }
  templateError.value = ''
  emit('sign-state-change', SIGN_STATUS_ENUM.PENDING_INITIATE)
}

const updateSignStateData = (data = {}) => {
  contractInfo.value = {
    ...contractInfo.value,
    configSignChannel: data?.signChannel ?? contractInfo.value.configSignChannel,
    signQrCode: data?.signQrCode || '',
    contractFileUrl: data?.contractFileUrl || '',
    signChannelName: data?.signChannelName || contractInfo.value.signChannelName,
    flowState: data?.flowState ?? contractInfo.value.flowState,
    flowStateName: data?.flowStateName || contractInfo.value.flowStateName,
  }
  emit('sign-state-change', Number(contractInfo.value.flowState))
}

const syncQrDrawerVisibleByFlowState = (flowState) => {
  const currentFlowState = Number(flowState)
  if (
    currentFlowState === SIGN_STATUS_ENUM.PENDING_INITIATE
    || currentFlowState === SIGN_STATUS_ENUM.SIGN_COMPLETED
  ) {
    qrDrawerVisible.value = false
  }
}

const getInitData = async () => {
  if (!props.orderId) {
    emit('sign-state-change', SIGN_STATUS_ENUM.PENDING_INITIATE)
    return
  }

  const { code, data } = await getExchangeCarSignConfig({
    orderId: props.orderId,
  })

  if (code === 1) {
    const contractTemplate = data?.contractTemplate || {}
    contractInfo.value = {
      merchantName: data?.orgName || '',
      customerName: data?.driverName || '',
      licenceNo: data.licenceNo || '',
      configSignChannel: data?.signChannel ?? '',
      signQrCode: data?.signQrCode || '',
      contractFileUrl: data?.contractFileUrl || '',
      signChannelName: data?.signChannelName || '',
      flowState: data?.flowState ?? '',
      flowStateName: data?.flowStateName || '',
      esignAuthorized: Boolean(data?.esignAuthorized),
    }
    emit('sign-state-change', Number(contractInfo.value.flowState))

    formData.value = {
      ...formData.value,
      signChannel: getDefaultSignChannel(data),
      changeDate: data?.changeDate ? new Date(data.changeDate) : formData.value.changeDate,
      templateId: contractTemplate?.id ?? contractTemplate?.templateId ?? formData.value.templateId,
      templateName: contractTemplate?.name || contractTemplate?.templateName || formData.value.templateName,
    }
  } else {
    emit('sign-state-change', SIGN_STATUS_ENUM.PENDING_INITIATE)
  }

  nextTick(() => {
    formRef.value?.clearValidate()
  })
}

const refreshConfig = async () => {
  await getInitData()
}

const refreshQrCodeStatus = async () => {
  if (!props.orderId) {
    Message.error('未获取到换车记录ID')
    return
  }

  refreshQrLoading.value = true
  const { code, data } = await getExchangeCarSignState({
    orderId: props.orderId,
  }).finally(() => {
    refreshQrLoading.value = false
  })

  if (code === 1) {
    updateSignStateData(data)
    syncQrDrawerVisibleByFlowState(data?.flowState)
  }
}

const openCancelSignConfirm = async () => {
  // Bump z-index after MessageBox renders to ensure it's above qs-drawer
  nextTick(() => {
    requestAnimationFrame(() => {
      const wrappers = document.querySelectorAll('.el-message-box__wrapper')
      const lastWrapper = wrappers[wrappers.length - 1]
      if (lastWrapper) {
        lastWrapper.style.setProperty('z-index', '9999', 'important')
      }
    })
  })

  try {
    await MessageBox.confirm('确认撤回签署？', '提示', {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning',
    })
    return true
  } catch (error) {
    return false
  }
}

const cancelSignHandle = async () => {
  if (!props.orderId) {
    Message.error('未获取到订单ID')
    return false
  }

  const confirmed = await openCancelSignConfirm()
  if (!confirmed) {
    return false
  }

  cancelSignLoading.value = true
  try {
    const { code } = await cancelExchangeCarSign({
      orderId: props.orderId,
    })
    if (code === 1) {
      qrDrawerVisible.value = false
      await getInitData()
      Message.success('撤回成功')
    }
  } finally {
    cancelSignLoading.value = false
  }

  return true
}

const handleAttachmentChange = () => {
  nextTick(() => {
    formRef.value?.validateField('attachment')
  })
}

const goToPreviewTemplate = async () => {
  if (!formData.value.templateId) {
    return
  }

  const { code, data } = await contractTemplateEffectPreviewV3Api({
    id: formData.value.templateId,
  })

  if (code === 1 && data) {
    window.open(data, '_blank')
  }
}

const validateTemplate = async () => {
  if (formData.value.signChannel !== SIGN_CHANNEL_MAP.ONLINE) {
    templateError.value = ''
    return true
  }

  const valid = Boolean(formData.value.templateId && formData.value.templateName)
  templateError.value = valid ? '' : '未获取到合同模板'
  return valid
}

const validateForm = async () => {
  await formRef.value.validate()

  const templateValid = await validateTemplate()
  if (!templateValid) {
    throw new Error('校验失败')
  }
}

const getFormData = async () => {
  await validateForm()

  return {
    ...formData.value,
    ...contractInfo.value,
  }
}

const requestSign = async () => {
  const submitData = await getFormData()

  if (submitData.signChannel !== SIGN_CHANNEL_MAP.ONLINE) {
    Message.warning('线下签署无需发起，请直接点击下一步')
    return false
  }

  if (!props.orderId) {
    Message.error('未获取到订单ID')
    return false
  }

  if (getRequestSignChannel() === SIGN_CHANNEL_MAP.ONLINE) {
    const { code, data } = await getExchangeCarSignState({
      orderId: props.orderId,
    })

    if (code === 1) {
      updateSignStateData(data)
      qrDrawerVisible.value = true
      return true
    }

    return false
  }

  const { code, data } = await genExchangeCarQrCode({
    orderId: props.orderId,
    changeDate: formatChangeDateForSubmit(submitData.changeDate),
  })

  if (code === 1) {
    updateSignStateData(data)
    qrDrawerVisible.value = true
    return true
  }

  return false
}

const isOnlineSignCompleted = () => {
  return Number(contractInfo.value.flowState) === SIGN_STATUS_ENUM.SIGN_COMPLETED
}

const getAttachmentUrl = () => {
  return formData.value.attachment?.[0]?.url || ''
}

const getRequestSignChannel = () => {
  return Number(contractInfo.value.configSignChannel)
}

const submitBeforeNextStep = async () => {
  const submitData = await getFormData()

  if (submitData.signChannel === SIGN_CHANNEL_MAP.ONLINE) {
    if (!isOnlineSignCompleted()) {
      Message.warning('请先完成线上签署')
      return false
    }
    return true
  }

  if (!props.orderId) {
    Message.error('未获取到订单ID')
    return false
  }

  const { code } = await offlineExchangeCarSign({
    orderId: props.orderId,
    changeDate: formatChangeDateForSubmit(submitData.changeDate),
    attachmentUrl: getAttachmentUrl(),
  })

  return code === 1
}

onMounted(() => {
  getInitData()
})

watch(() => props.orderId, () => {
  resetData()
  getInitData()
})


watch(() => formData.value.signChannel, () => {
  emit('sign-channel-change', formData.value.signChannel)
  templateError.value = ''
  nextTick(() => {
    formRef.value?.clearValidate('signChannel')
    formRef.value?.clearValidate('attachment')
  })
}, { immediate: true })

defineExpose({
  getFormData,
  requestSign,
  refreshConfig,
  submitBeforeNextStep,
  isOnlineSignCompleted,
})
</script>

<style lang="scss" scoped>
.sign-contract-container {
  padding: 0 18px;
}

.sign-contract-form {
  ::v-deep .el-form-item__label {
    display: block;
    float: none;
    padding: 0 0 10px 0;
    text-align: left;
    line-height: 22px;
  }

  ::v-deep .el-form-item__content {
    margin-left: 0 !important;
    text-align: left;
    line-height: 1.5;
  }
}

.readonly-text {
  color: #303133;
}

.template-select-box {
  padding-top: 30px;
}

.template-error {
  margin-top: 4px;
  color: #f56c6c;
  font-size: 12px;
  line-height: 1;
}

.upload-tip {
  margin-top: 8px;
  color: #909399;
  font-size: 14px;
  line-height: 22px;
}

.qr-drawer-content {
  padding: 0 20px;
}

.sign-instructions {
  padding: 12px 16px;
  background-color: #f7f8fa;
  border: 1px solid #e5e6eb;
  border-radius: 8px;
  margin-bottom: 16px;

  .top-info {
    display: flex;
    align-items: flex-start;
    gap: 8px;
  }

  .info-icon {
    color: #3b82f6;
    font-size: 16px;
    margin-top: 2px;
    flex-shrink: 0;
  }

  .info-content {
    flex: 1;
  }

  .info-text {
    color: #4e5969;
    font-size: 14px;
    line-height: 22px;
  }
}

.qr-code-section {
  display: flex;
  justify-content: center;
}

.qr-code-container {
  text-align: center;
}

.qr-code-info {
  margin-bottom: 10px;
}

.plate-no {
  color: #1d2129;
  font-size: 18px;
  font-weight: 600;
}

.qr-desc {
  font-size: 16px;
  color: #909399;
}

.qr-code {
  width: 260px;
  height: 260px;
  margin: 0 auto 16px;
  border: 1px solid #e4e7ed;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f5f7fa;
}

.qr-placeholder {
  width: 240px;
  height: 240px;
  border-radius: 6px;
}

.qr-code-empty {
  color: #909399;
  font-size: 14px;
}

.preview-box {
  width: 100%;
  height: 48px;
  border: 1px solid #ebeef5;
  border-radius: 8px;
  padding: 0 16px;
  display: flex;
  align-items: center;
  background: #fff;

  &__icon {
    width: 16px;
    height: 16px;
    background-image: url('~@/assets/img/QS-ic-sc-excel@2x.png');
    background-size: contain;
    background-repeat: no-repeat;
    margin-right: 8px;
    flex-shrink: 0;
  }

  &__content {
    color: #2a6bff;
    font-size: 14px;
    flex: 1;
    min-width: 0;

    &__text {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  &__view {
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-left: 8px;
    cursor: pointer;
    color: #909399;

    .QS-icon-view {
      font-size: 16px;
    }
  }
}
</style>
